import { z } from 'zod';
import { normaliseReferralCode } from '@packages/types';

/**
 * First-touch referral capture.
 *
 * A referral link is `<app>/signup?ref=<CODE>`. The app keeps the first code it
 * sees for 60 days and claims it once onboarding has created the account. It
 * lives in localStorage, unlike campaign attribution, because a colleague often
 * opens the link days before signing up.
 */

const STORAGE_KEY = 'referral_capture';
export const REFERRAL_CAPTURE_TTL_MS = 60 * 24 * 60 * 60 * 1000;

const StoredReferralSchema = z.object({ code: z.string(), capturedAt: z.number() });
type StoredReferral = z.infer<typeof StoredReferralSchema>;

/** Stores the `ref` code in `search` unless an unexpired first touch is already stored. */
export function captureReferral(search: string, now: number = Date.now()): void {
  const code = normaliseReferralCode(new URLSearchParams(search).get('ref') ?? '');
  if (!code) return;
  try {
    if (readStored(now)) return;
    const entry: StoredReferral = { code, capturedAt: now };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Storage unavailable (private mode, quota): the signup goes unreferred.
  }
}

/** The stored code, or undefined when there is none, it has expired, or it does not parse. */
export function readReferralCode(now: number = Date.now()): string | undefined {
  try {
    return readStored(now)?.code;
  } catch {
    return undefined;
  }
}

export function clearReferralCode(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored that could be read back either.
  }
}

function readStored(now: number): StoredReferral | undefined {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === null) return undefined;
  const parsed = StoredReferralSchema.safeParse(JSON.parse(raw));
  if (!parsed.success) return undefined;
  const code = normaliseReferralCode(parsed.data.code);
  if (!code || now - parsed.data.capturedAt > REFERRAL_CAPTURE_TTL_MS) return undefined;
  return { code, capturedAt: parsed.data.capturedAt };
}
